import "./style.scss";

import { Warning } from "@mui/icons-material";
import { Autocomplete, FormControlLabel, Grid, Radio, RadioGroup, Tooltip } from "@mui/material";
import type { TEditingBeneficiarProfile } from "components/BPSettings/types";
import type { TMissingInfos } from "components/shared/BPNavigationBarSettings";
import LabelComponent from "components/shared/LabelComponent";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { placeTextAreaAtEnd } from "containers/CustomFieldsContainer/helpers";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import { useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ICustomField, StoreCustomField } from "store/customFieldsStore";
import type { CountryFeatures } from "types/countryFeatures";

import BeneficiaryCustomFields from "../BeneficiaryCustomFields";
import MissingInfosWarning from "../MissingInfosWarning";
import { getMissingFieldsNames } from "../utils";

type TProps = {
    /**
     * Data of the specific page.
     */
    data: TEditingBeneficiarProfile["tax"] | undefined;
    /**
     * Modifies editingProfile whenever there is a change that is made to reflect it visually and value is ready in case changes are saved.
     * @param ev Data of event triggered.
     * @param data Data of the specific page (so editingProfile.account for instance).
     * @param customFieldsInfo Data needed to update + format CF: data = fullCustomFieldsData, field = field saved in Benef info, fullFieldData = data of CF as saved in store.
     */
    handleChange: (
        ev: any,
        data: any,
        customFieldsInfo?: { data: any; field: any; fullFieldData?: ICustomField },
    ) => void;
    /** Country features driving which tax fields to display */
    countryFeatures: CountryFeatures;
    /**
     * Know if changes are allowed
     */
    allowChanges: boolean;
    handleSocialTypeChange: (value: { type: string; description: string }, data: any) => void;
    /**
     * Custom fields existing on this company:
     */
    companyBenefCustomFields: ICustomField[] | undefined;
    /**
     * Handles saving fileData into editingProfile value:
     */
    handleFileSaving: (data: any, fileData: string, field: any, fullFieldData: ICustomField) => void;
    /**
     * Custom fields data that exist on company with the benef info:
     */
    customFieldsData: StoreCustomField[] | undefined;
    /**
     * Verify if the author got invited to complete their profile
     */
    isInvited: boolean;
    /**
     * Current company id
     */
    companyId: number;
};

export const socialProfileUnseparatedList = [
    { type: "1", description: "Artistes-Auteurs Assujettis aux précomptes" },
    { type: "2", description: "Artistes-Auteurs Dispensés de précomptes" },
    { type: "3", description: "Artistes-Auteurs résidents fiscaux étrangers" },
    { type: "4", description: "Artistes-Auteurs Décédés" },
    { type: "5", description: "Diffuseur Facturation des Contributions 1,10%" },
    { type: "6", description: "Diffuseur Facturation du Droit d'utilisation" },
    {
        type: "7",
        description: "Artistes-Auteurs Rémunération du Droit d'exploitation",
    },
    { type: "8", description: "Artistes-Auteurs Rémunération du Droit de prêt" },
    {
        type: "9",
        description: "Artistes-Auteurs Rémunération du droit de reprographie",
    },
    {
        type: "L",
        description: "Artistes-Auteurs Revenus précomptés non soumis à contribution diffuseur",
    },
    // {type: "A", description: "THC Artistes Auteurs Assujettis aux précomptes"},
    // {type: "B", description: "THC Artistes Auteurs Dispensés de Précomptes"},
    // {type: "C", description: "DCA Artistes Auteurs Dispensés de Précomptes"},
    // {type: "D", description: "DCA Artistes Auteurs Décédés, ou Non résidents fiscaux, ou anonyme ou OEuvre tombée dans le domaine public"},
    // {type: "H", description: "OVV Artistes Auteurs Dispensés de Précomptes"},
    // {type: "I", description: "OVV Artistes Auteurs Décédés, ou Non résidents fiscaux, ou anonyme ou OEuvre tombée dans le domaine public"},
];

const BPTaxProfile = ({
    data,
    handleChange,
    countryFeatures,
    allowChanges,
    handleSocialTypeChange,
    companyBenefCustomFields,
    handleFileSaving,
    customFieldsData,
    isInvited,
    companyId,
}: TProps) => {
    const [t] = useTranslation();
    const { getBPCompanySettings } = useBPCompanySettings();
    const usesTwentyPercentVat = getBPCompanySettings("uses_twenty_percent_vat", companyId);

    const [socialProfileType, setSocialProfileType] = useState(
        socialProfileUnseparatedList.find((item) => item.type == data?.socialProfileType),
    );

    const taxCustomFields = useMemo(() => {
        const neededFields = companyBenefCustomFields?.filter((field) => field.formLocation === "accounting");
        return placeTextAreaAtEnd(neededFields);
    }, [companyBenefCustomFields]);

    // Find mandatory custom fields + check if value is empty ("")
    const emptyMandatoryCustomFields =
        taxCustomFields?.filter(
            (field) =>
                !!field.isRequired &&
                !customFieldsData?.find((fieldData) => fieldData.customFieldTypeId === field.id)?.value,
        ) ?? [];

    const renderInputAutocomplete = (params: any) => {
        return <StyledTextField {...params} placeholder={t("BeneficiaryPortal.TaxPage.ChooseSocialProfile")} />;
    };

    const handleProfileChange = (e: any, value: any) => {
        setSocialProfileType(value);
        handleSocialTypeChange(value, { ...data });
    };

    const missingFieldsNames: string[] = getMissingFieldsNames(emptyMandatoryCustomFields);

    const hasEmptyMandatoryInfo = isInvited && !!emptyMandatoryCustomFields.length;

    const missingInfos: TMissingInfos = {
        tax: emptyMandatoryCustomFields,
    };

    return (
        <div className="bp-settings-info">
            {hasEmptyMandatoryInfo && (
                <div className="bp-settings-info-bloc" style={{ marginBottom: "0" }}>
                    <MissingInfosWarning
                        tooltipTitle={missingFieldsNames.join(", ")}
                        from="tax"
                        missingInfos={missingInfos}
                    />
                </div>
            )}
            <div className="bp-settings-info-bloc">
                <h3 style={{ marginTop: "2.75rem" }}>{t("NavigationBar.Management")}</h3>

                {/* Direct precount (Belgium, …): show radio when options exist */}
                {countryFeatures.precountMode === "direct" && countryFeatures.precount.options.length > 0 && (
                    <div className="bp-settings-tax-bloc">
                        <LabelComponent htmlFor="radio-group-precount">{t("Beneficiars.Precount")}</LabelComponent>
                        <div>
                            <p>{t("Settings.PrecountDesc")}</p>
                            <RadioGroup
                                aria-labelledby="radio-group-precount"
                                name="precount"
                                row
                                value={data ? data.precount : ""}
                                onChange={(ev) => handleChange(ev, { ...data })}
                            >
                                {countryFeatures.precount.options.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={String(opt.value)}
                                        disabled={!allowChanges}
                                        control={<Radio />}
                                        label={t(opt.label)}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    </div>
                )}

                {/* Social profile type mode (France): show precount radio until profile type is chosen */}
                {countryFeatures.precountMode === "social_profile_type" && !data?.socialProfileType && (
                    <div className="bp-settings-tax-bloc">
                        <LabelComponent htmlFor="radio-group-precount">{t("Beneficiars.Precount")}</LabelComponent>
                        <div>
                            <p>{t("Settings.PrecountDesc")}</p>
                            <RadioGroup
                                aria-labelledby="radio-group-precount"
                                name="precount"
                                row
                                value={data ? data.precount : ""}
                                onChange={(ev) => handleChange(ev, { ...data })}
                            >
                                {countryFeatures.precount.options.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={String(opt.value)}
                                        disabled={!allowChanges}
                                        control={<Radio />}
                                        label={t(opt.label)}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    </div>
                )}
                {countryFeatures.precountMode === "social_profile_type" && !!data?.socialProfileType && (
                    <div className="bp-settings-tax-bloc">
                        <LabelComponent htmlFor="radio-group-precount">{t("Settings.SocialProfile")}</LabelComponent>
                        <div>
                            <p>{t("Settings.SocialProfileDesc")}</p>
                            <Autocomplete
                                size="small"
                                value={socialProfileType}
                                options={socialProfileUnseparatedList}
                                // A profile type is mandatory once set: clearing would send a null option downstream
                                disableClearable
                                onChange={(e, value) => handleProfileChange(e, value)}
                                getOptionLabel={(option) => option.description}
                                renderInput={(params) => renderInputAutocomplete(params)}
                                disabled={!allowChanges}
                                componentsProps={{
                                    popper: { className: "autocomplete-form-component" },
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* VAT: driven by country features options */}
                {countryFeatures.vat.options.length > 0 && (
                    <div className="bp-settings-tax-bloc">
                        <LabelComponent htmlFor="">{t("Beneficiars.VAT")}</LabelComponent>
                        <div>
                            <p>{t("Settings.TVADesc")}</p>
                            <RadioGroup
                                aria-labelledby="radio-group-tva"
                                name="vat"
                                row
                                value={data ? data.vat : ""}
                                onChange={(ev) => handleChange(ev, { ...data })}
                            >
                                {countryFeatures.vat.options
                                    .filter((opt) => opt.value !== 3 || !!usesTwentyPercentVat)
                                    .map((opt) => (
                                        <FormControlLabel
                                            key={opt.value}
                                            value={String(opt.value)}
                                            disabled={!allowChanges}
                                            control={<Radio />}
                                            label={t(opt.label)}
                                        />
                                    ))}
                            </RadioGroup>
                        </div>
                    </div>
                )}
            </div>
            {taxCustomFields && taxCustomFields.length > 0 && (
                <div className="bp-settings-info-bloc custom-fields tax-custom-fields">
                    <h3>{t("BeneficiaryPortal.TaxPage.OtherInfo")}</h3>
                    <Grid container rowSpacing={4} columnSpacing={2}>
                        {taxCustomFields.map((field) => {
                            const fieldData = customFieldsData?.find((item) => item.customFieldTypeId === field.id);
                            return (
                                <BeneficiaryCustomFields
                                    key={`field_${fieldData?.customFieldTypeId}`}
                                    field={fieldData}
                                    data={data}
                                    fullFieldData={field}
                                    handleChange={handleChange}
                                    handleFileSaving={handleFileSaving}
                                    customFieldsData={customFieldsData}
                                />
                            );
                        })}
                    </Grid>
                </div>
            )}
            {!allowChanges && (
                <div className="bp-settings-info-bloc">
                    <div className="bp-settings-tax-bloc bp-settings-tax-info-plus">
                        <LabelComponent htmlFor="">
                            <Warning /> {t("BPSettings.Important")}
                        </LabelComponent>
                        <p>{t("BPSettings.TaxProfilInfoText")}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BPTaxProfile;
